import React, { useState } from 'react';


function formatPHP(value) {
  if (value >= 1000000) return `₱${(value / 1000000)?.toFixed(1)}M`;
  if (value >= 1000) return `₱${(value / 1000)?.toFixed(0)}K`;
  return `₱${value?.toLocaleString()}`;
}

export default function RegionRankingPanel({ regions, selectedRegion, onSelectRegion, activeMetric }) {
  const [sortBy, setSortBy] = useState('revenue');

  const sorted = [...regions]?.sort((a, b) => {
    if (sortBy === 'revenue') return b?.revenue - a?.revenue;
    if (sortBy === 'growth') return b?.growth - a?.growth;
    if (sortBy === 'coverage') return b?.coverage - a?.coverage;
    return b?.orders - a?.orders;
  });

  const metricValue = (r) => {
    if (sortBy === 'revenue') return formatPHP(r?.revenue);
    if (sortBy === 'growth') return `${r?.growth > 0 ? '+' : ''}${r?.growth}%`;
    if (sortBy === 'coverage') return `${r?.coverage}%`;
    return r?.orders?.toLocaleString();
  };

  const metricColor = (r) => {
    if (sortBy === 'growth') return r?.growth >= 0 ? 'text-emerald-600' : 'text-red-600';
    if (sortBy === 'coverage') return r?.coverage >= 80 ? 'text-emerald-600' : r?.coverage >= 60 ? 'text-amber-600' : 'text-red-600';
    return 'text-blue-700';
  };

  return (
    <div className="border border-border rounded-xl shadow-sm overflow-hidden flex flex-col h-full" style={{ background: 'var(--color-card)' }}>
      <div className="p-4 border-b border-border">
        <h2 className="font-heading font-semibold text-foreground text-sm mb-3">Regional Rankings</h2>
        <div className="flex gap-1 flex-wrap">
          {[
            { value: 'revenue', label: 'Revenue' },
            { value: 'growth', label: 'Growth' },
            { value: 'coverage', label: 'Coverage' },
            { value: 'orders', label: 'Orders' },
          ]?.map((s) => (
            <button
              key={s?.value}
              onClick={() => setSortBy(s?.value)}
              className={`px-2.5 py-1 rounded-md text-xs font-caption font-medium transition-all duration-200 press-effect`}
              style={sortBy === s?.value ? { background: 'linear-gradient(135deg, #DC2626, #B91C1C)', color: '#FFFFFF', boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)' } : { background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}
            >
              {s?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {sorted?.map((region, idx) => (
          <button
            key={region?.id}
            onClick={() => onSelectRegion(region)}
            className={`w-full flex items-center gap-3 px-4 py-3 border-b border-border text-left transition-all duration-200 press-effect`}
            style={selectedRegion?.id === region?.id ? { background: 'rgba(220, 38, 38, 0.08)', borderLeft: '2px solid #DC2626' } : { background: 'transparent' }}
            onMouseEnter={e => { if (selectedRegion?.id !== region?.id) e.currentTarget.style.background = 'var(--color-muted)'; }}
            onMouseLeave={e => { if (selectedRegion?.id !== region?.id) e.currentTarget.style.background = 'transparent'; }}
          >
            <span className={`font-data text-sm font-bold w-6 flex-shrink-0`} style={{ color: idx < 3 ? '#D4AF37' : 'var(--color-muted-foreground)' }}>
              #{idx + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-caption text-sm font-medium text-foreground truncate">{region?.name}</p>
              <p className="font-caption text-xs text-muted-foreground">{region?.island} • {region?.accounts} accounts</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className={`font-data text-sm font-bold`} style={{ color: sortBy === 'growth' ? (region?.growth >= 0 ? '#22C55E' : '#EF4444') : sortBy === 'coverage' ? (region?.coverage >= 80 ? '#22C55E' : region?.coverage >= 60 ? '#D4AF37' : '#EF4444') : '#DC2626' }}>{metricValue(region)}</p>
              <div className="flex items-center justify-end gap-0.5 mt-0.5">
                <div
                  className="h-1.5 rounded-full bg-blue-200"
                  style={{ width: '40px' }}
                >
                  <div
                    className="h-1.5 rounded-full bg-blue-700 transition-all duration-500"
                    style={{ width: `${Math.min((region?.revenue / sorted?.[0]?.revenue) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
      {/* Summary Footer */}
      <div className="p-3 border-t border-slate-100 bg-slate-50">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="font-caption text-xs text-slate-500">Total Revenue</p>
            <p className="font-data text-sm font-bold text-blue-800">
              {formatPHP(regions?.reduce((s, r) => s + r?.revenue, 0))}
            </p>
          </div>
          <div>
            <p className="font-caption text-xs text-slate-500">Avg Coverage</p>
            <p className="font-data text-sm font-bold text-emerald-700">
              {Math.round(regions?.reduce((s, r) => s + r?.coverage, 0) / regions?.length)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}